import { FORM_VALIDATOR_TYPES } from '../constants';
import { IStudentFormData, STUDENT_STATUS } from '../interface/studentModel';
import { tr } from '../i18n/i18n';
import { translateLookupValue } from '../utility/i18nLookup';

export const bloodTypes = [
  { label: 'A+', code: 'A+' },
  { label: 'A-', code: 'A-' },
  { label: 'B+', code: 'B+' },
  { label: 'B-', code: 'B-' },
  { label: 'AB+', code: 'AB+' },
  { label: 'AB-', code: 'AB-' },
  { label: 'O+', code: 'O+' },
  { label: 'O-', code: 'O-' },
];

/**
 * Evaluated per call, not at import. As a module-level const its tr() ran once
 * when the chunk first loaded — before the language was necessarily resolved —
 * and never updated on a language switch (CVA-4513).
 */
export const getGenderOptions = () => [
  { label: tr('ui.male', 'Male'), code: 'Male' },
  { label: tr('ui.female', 'Female'), code: 'Female' },
];

/**
 * Display labels for every student lifecycle status.
 *
 * English-only by design: this is a module-level constant, so wrapping the
 * values in tr() would freeze them at whatever language was active when the
 * module first loaded and they would never update on a language switch.
 * Render with getStudentStatusLabel() below instead.
 */
export const STUDENT_STATUS_LABELS: Record<STUDENT_STATUS, string> = {
  [STUDENT_STATUS.ACTIVE]: 'Active',
  [STUDENT_STATUS.INACTIVE]: 'Inactive',
  [STUDENT_STATUS.GRADUATED]: 'Graduated',
  [STUDENT_STATUS.DROP_OFF]: 'Drop Off',
};

/**
 * Localised label for a student lifecycle status. A function (not a static
 * map) so each call re-reads the catalog against the current language.
 * Resolves through the shared `status.*` catalog that StatusTag already uses —
 * ACTIVE -> status.active, DROP_OFF -> status.drop_off — and falls back to the
 * English label, then the raw value, for anything not in the catalog.
 *
 * Use this for PLAIN TEXT only. Do NOT feed the result to <StatusTag>: it
 * localises the label itself and picks its colour by switching on the English
 * literal, so handing it a translated string drops the chip to default grey.
 * Pass StatusTag the English STUDENT_STATUS_LABELS value instead.
 */
export const getStudentStatusLabel = (status?: STUDENT_STATUS | string | null): string => {
  if (!status) return '';
  return translateLookupValue(
    'status',
    STUDENT_STATUS_LABELS[status as STUDENT_STATUS] ?? String(status),
  );
};

/**
 * Text color classes per student lifecycle status (used by list-page status columns).
 */
export const STUDENT_STATUS_TEXT_CLASSES: Record<STUDENT_STATUS, string> = {
  [STUDENT_STATUS.ACTIVE]: 'text-green-600',
  [STUDENT_STATUS.INACTIVE]: 'text-purple-600',
  [STUDENT_STATUS.GRADUATED]: 'text-blue-600',
  [STUDENT_STATUS.DROP_OFF]: 'text-orange-600',
};

/**
 * Terminal "school leaver" statuses. These are read-only in profile forms and
 * can only be reached through the re-enrollment GRADUATE/DROP_OFF transition.
 */
export const TERMINAL_STUDENT_STATUSES: STUDENT_STATUS[] = [
  STUDENT_STATUS.GRADUATED,
  STUDENT_STATUS.DROP_OFF,
];

export const isTerminalStudentStatus = (status?: STUDENT_STATUS | string | null): boolean =>
  TERMINAL_STUDENT_STATUSES.includes(status as STUDENT_STATUS);

/**
 * Statuses an admin can set from student forms (terminal states are excluded —
 * they are set exclusively by the re-enrollment transition flow).
 */
export const studentStatusOptions = [
  { label: STUDENT_STATUS_LABELS[STUDENT_STATUS.ACTIVE], code: STUDENT_STATUS.ACTIVE },
  { label: STUDENT_STATUS_LABELS[STUDENT_STATUS.INACTIVE], code: STUDENT_STATUS.INACTIVE },
];

export const STUDENT_FORM_INIT: IStudentFormData = {
  status: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.OBJECT,
    disable: false,
  },
  studentId: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: true, // Always read-only
  },
  firstName: {
    value: '',
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  lastName: {
    value: '',
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  displayName: {
    value: '',
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  email: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.EMAIL,
    disable: false,
  },
  curriculum: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  grade: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  requestedGrade: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  classroom: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  gender: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.OBJECT,
    disable: false,
  },
  dob: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.DATE,
    disable: false,
  },
  address: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  blood: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.OBJECT,
    disable: false,
  },
  admissionDate: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.DATE,
    disable: false,
  },
  admissionNo: {
    value: '',
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  medication: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  allergies: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  emergencyName: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  emergencyContact: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  // CVA-4629: optional. Length and character rules are checked separately, so
  // the generic validator only has to leave a blank value alone.
  nationalId: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
};
